import { inject, Injectable } from '@angular/core';
import type { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, type Observable, timer } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';

import { AuthStore } from './auth.store';

/**
 * Preloading strategy: fetch every lazy page chunk in the background,
 * but only once the user is authenticated.
 *
 * This is the load-time half of the "navigation feels slow" fix. Both
 * apps route entirely through lazy `loadComponent`, so before this the
 * *first* visit to each screen paid a chunk fetch before Angular could
 * render anything — the click looked ignored for as long as the network
 * took. Preloading moves that fetch into idle time after the shell is up,
 * so by the time anyone clicks Leads or Jobs the chunk is already in
 * memory and the navigation is effectively instant. The progress bar then
 * only ever appears for genuinely slow cases, which is what makes it
 * meaningful rather than decorative.
 *
 * Why not `PreloadAllModules`: it walks the whole route config regardless
 * of `canMatch`, which would pull every protected chunk down for
 * anonymous visitors on the login screen — exactly the property the
 * `authMatchGuard`-at-`canMatch` arrangement in `app.routes.ts` exists to
 * preserve, and wasted bandwidth for anyone who never signs in. Gating on
 * `AuthStore.isAuthenticated()` keeps that intact.
 *
 * The `PRELOAD_DELAY_MS` head start keeps preloading off the critical
 * path: the landing page's own chunk and its first API calls get the
 * connection to themselves, and the background fetches start once that
 * burst is done. Without it the preloader competes with the very
 * navigation it is meant to make faster.
 */
@Injectable({ providedIn: 'root' })
export class AuthAwarePreloadStrategy implements PreloadingStrategy {
  /** Head start given to the initial route before background fetching begins. */
  private static readonly PRELOAD_DELAY_MS = 1500;

  private readonly store = inject(AuthStore);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // Opt a route out with `data: { preload: false }` — worth doing for
    // anything heavy that most users never open.
    if (route.data?.['preload'] === false) return EMPTY;
    if (!this.store.isAuthenticated()) return EMPTY;

    return timer(AuthAwarePreloadStrategy.PRELOAD_DELAY_MS).pipe(
      take(1),
      mergeMap(() => load()),
    );
  }
}
